<template>
  <div class="w-full max-w-md p-8">
    <h1 class="font-[700] text-3xl text-center text-mx-ink mb-[1vh]">
      {{ t('pages.forgotPassword.step3.title') }}
    </h1>

    <UForm method="post" :state="formState" :schema="schema" @submit="onSubmit" class="space-y-4">
      <UFormField name="password" :label="t('pages.forgotPassword.step3.form.password.label')" class="h-24">
        <UInput class="w-full" size="xl" v-model="formState.password" :type="isPasswordVisible ? 'text' : 'password'"
          :placeholder="t('pages.forgotPassword.step3.form.password.placeholder')" :disabled="isLoading"
          :ui="{ base: 'py-3 px-4', trailing: 'pr-5' }">
          <template v-if="formState.password" #trailing>
            <UIcon :name="isPasswordVisible ? 'i-lucide-eye' : 'i-lucide-eye-off'" class="w-6 h-6"
              @click="isPasswordVisible = !isPasswordVisible" />
          </template>
        </UInput>
      </UFormField>
      <UFormField name="confirmPassword" :label="t('pages.forgotPassword.step3.form.confirmPassword.label')"
        class="h-24">
        <UInput class="w-full" size="xl" v-model="formState.confirmPassword"
          :type="isConfirmPasswordVisible ? 'text' : 'password'"
          :placeholder="t('pages.forgotPassword.step3.form.confirmPassword.placeholder')" :disabled="isLoading"
          :ui="{ base: 'py-3 px-4', trailing: 'pr-5' }">
          <template v-if="formState.confirmPassword" #trailing>
            <UIcon :name="isConfirmPasswordVisible ? 'i-lucide-eye' : 'i-lucide-eye-off'" class="w-6 h-6"
              @click="isConfirmPasswordVisible = !isConfirmPasswordVisible" />
          </template>
        </UInput>
      </UFormField>
      <UButton type="submit" :disabled="!isHydrated" color="primary" size="xl" block :loading="isLoading" class="py-3">
        {{ t('pages.forgotPassword.step3.continue') }}
      </UButton>
    </UForm>
  </div>
</template>

<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui';
import { useMounted } from '@vueuse/core';
import * as yup from 'yup';
import { resetPasswordApi } from '~/api/auth';
import { useNotificationPolling } from '~/composables/useNotificationPolling';
import { useToastStore } from '~/stores/toast';
import type { ApiErrorWithStatus } from '~/types/api';
import { NotificationType } from '~/types/notifications';
import { useRouter } from '#app';
import { useLocalePath } from '#i18n';

const { t } = useI18n();
const router = useRouter();
const localePath = useLocalePath();
const isLoading = ref(false);
// hydration 完成前不讓表單送得出去，避免瀏覽器走原生提交把欄位帶進網址列
const isHydrated = useMounted();
const toastStore = useToastStore();
const { startPollingNotification } = useNotificationPolling();
const isPasswordVisible = ref(false);
const isConfirmPasswordVisible = ref(false);

const props = defineProps<{
  code: string;
  email: string;
}>();

const formState = ref({
  password: '',
  confirmPassword: '',
});

const schema = yup.object({
  password: yup
    .string()
    .required(t('pages.forgotPassword.step3.form.password.required'))
    .max(20, t('pages.forgotPassword.step3.form.password.maxLength'))
    .min(8, t('pages.forgotPassword.step3.form.password.minLength'))
    .matches(/[a-zA-Z]/, t('pages.forgotPassword.step3.form.password.letter'))
    .matches(/[0-9]/, t('pages.forgotPassword.step3.form.password.number'))
    .test('safe-characters', t('pages.forgotPassword.step3.form.password.characters'), (value) => {
      if (!value) return true;
      const allowedPattern = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{}|;:,.<>?]*$/;
      return allowedPattern.test(value);
    }),
  confirmPassword: yup
    .string()
    .required(t('pages.forgotPassword.step3.form.confirmPassword.required'))
    .oneOf([yup.ref('password')], t('pages.forgotPassword.step3.form.confirmPassword.match')),
});

async function onSubmit(event: FormSubmitEvent<typeof formState.value>) {
  isLoading.value = true;
  try {
    const payload = {
      code: props.code,
      email: props.email,
      password: event.data.password,
      second_password: event.data.confirmPassword,
    };
    await resetPasswordApi(payload);

    // 啟動輪詢檢查 password_reset_success 通知
    await startPollingNotification(NotificationType.PasswordResetSuccess);

    await router.push(localePath('/login'));
    toastStore.success(t('pages.forgotPassword.step3.success'));
  } catch (error) {
    const apiError = error as ApiErrorWithStatus | Error;
    if ('message' in apiError && apiError.message) {
      toastStore.error(apiError.message);
    } else {
      toastStore.error(t('pages.forgotPassword.step3.failed'));
    }
  } finally {
    isLoading.value = false;
  }
}
</script>